const MAX_DIMENSION = 1600;
const QUALITY = 0.8;

// Shrinks phone photos so multipart requests stay under the Cloud Functions body limit.
export async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (error) {
    return file;
  }
}

const isHeic = (file) =>
  /^image\/hei[cf]$/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');

// Browsers other than Safari can't display HEIC, so convert to JPEG as soon as the file is picked.
// libheif-js is used because heic2any's bundled decoder rejects HEIC files from newer iPhones.
export async function convertHeicToJpeg(file) {
  if (!file || !isHeic(file)) {
    return file;
  }

  try {
    const loaded = await import('libheif-js/wasm-bundle');
    const libheif = await (loaded.default || loaded);
    const decoder = new libheif.HeifDecoder();
    const [image] = decoder.decode(new Uint8Array(await file.arrayBuffer()));
    const width = image.get_width();
    const height = image.get_height();

    const full = document.createElement('canvas');
    full.width = width;
    full.height = height;
    const ctx = full.getContext('2d');
    const imageData = ctx.createImageData(width, height);
    await new Promise((resolve, reject) => {
      image.display(imageData, (result) => (result ? resolve(result) : reject(new Error('HEIC decode failed'))));
    });
    ctx.putImageData(imageData, 0, 0);

    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    const out = document.createElement('canvas');
    out.width = Math.round(width * scale);
    out.height = Math.round(height * scale);
    out.getContext('2d').drawImage(full, 0, 0, out.width, out.height);

    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) {
      return null;
    }

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (error) {
    return null;
  }
}

// Drops files that can't be converted and reports how many were skipped.
export async function prepareImages(files) {
  const converted = await Promise.all(files.map(convertHeicToJpeg));
  const ready = converted.filter(Boolean);
  return { ready, failed: converted.length - ready.length };
}

export const compressImages = (files) => Promise.all(files.map(compressImage));
